import { useEffect, useRef, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Peta untuk MEMILIH satu titik lokasi kerja.
 *
 * Diadaptasi dari aplikasi absensi-kurir Emguard. Tiga hal dibuat berbeda, dan ketiganya punya
 * alasan yang sama — layar ini milik ADMIN, dan biayanya tidak boleh menyebar ke kurir:
 *
 * 1. **Leaflet polos, tanpa `react-leaflet`.** Satu dependensi lebih sedikit dan tidak ada risiko
 *    kompatibilitas React 19. Peta ini tidak butuh reaktivitas apa pun selain "pindahkan penanda".
 *
 * 2. **Lingkaran, bukan pin.** Penanda bawaan Leaflet memuat berkas gambar yang jalurnya sering
 *    rusak setelah bundling — aplikasi lama sampai punya `leafletIkon.js` khusus untuk menambalnya.
 *    Lingkaran digambar Leaflet sendiri, jadi tidak ada aset yang bisa hilang. Ia juga LEBIH
 *    informatif: yang penting bagi geofence bukan titiknya, melainkan seberapa luas radiusnya.
 *
 * 3. **Hanya dimuat di layar ini.** Ubin peta ditarik dari jaringan; kurir yang membuka layar
 *    absen tidak membayarnya.
 */
export function MapPicker({
  latitude,
  longitude,
  radiusMeters,
  onPick,
}: {
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly radiusMeters: number;
  readonly onPick: (latitude: number, longitude: number) => void;
}): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  // Disimpan di ref agar peta tidak perlu dibuat ulang setiap render hanya karena handler berubah.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || mapRef.current !== null) return;

    // Jakarta sebagai titik awal bila belum ada pilihan — bukan (0,0), yang berada di laut lepas
    // Ghana dan membuat admin mengira petanya rusak.
    const awal: L.LatLngExpression = [latitude ?? -6.2, longitude ?? 106.816666];
    const map = L.map(container).setView(awal, latitude === null ? 11 : 16);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', (event: L.LeafletMouseEvent) => {
      onPickRef.current(event.latlng.lat, event.latlng.lng);
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      circleRef.current = null;
    };
    // Daftar dependensi sengaja KOSONG: `latitude`/`longitude` di sini hanya menentukan tampilan
    // AWAL peta. Memasukkannya akan membuat peta dibongkar-pasang setiap kali admin menggeser
    // titik — kehilangan tingkat zoom dan posisi geser yang baru saja ia atur. Perubahan titik
    // ditangani efek di bawah, yang memindahkan lingkaran tanpa menyentuh petanya.
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (map === null) return;

    if (latitude === null || longitude === null) {
      circleRef.current?.remove();
      circleRef.current = null;
      return;
    }

    const posisi: L.LatLngExpression = [latitude, longitude];
    if (circleRef.current === null) {
      circleRef.current = L.circle(posisi, {
        radius: radiusMeters,
        color: '#14161a',
        weight: 2,
        fillColor: '#14161a',
        fillOpacity: 0.12,
      }).addTo(map);
      map.setView(posisi, 16);
    } else {
      circleRef.current.setLatLng(posisi);
      circleRef.current.setRadius(radiusMeters);
    }
  }, [latitude, longitude, radiusMeters]);

  return (
    <div
      ref={containerRef}
      className="map-picker"
      // Peta adalah gambar interaktif; pembaca layar tidak dapat memakainya. Kolom koordinat di
      // sebelahnya yang menjadi jalur setara — dinyatakan di sini agar tidak dikira terlewat.
      role="application"
      aria-label="Peta pemilih titik lokasi kerja. Koordinat terpilih ditampilkan sebagai teks di bawah peta."
    />
  );
}
